import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles,
  MapPin,
  Building2,
  Map,
  X,
  Check,
  Rocket,
  TrendingUp,
  Eye,
  Clock,
  Coins,
  CreditCard,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../lib/toast';

// Tier presentation metadata (icon + display order). Pricing is fetched from
// the backend — see GET /api/inventory/boost-pricing.
const TIER_META = {
  local:    { label: 'Local',    icon: MapPin,    description: 'Within 15 km',  features: ['Shown to nearby buyers', 'Best for local pick-up items'] },
  metro:    { label: 'Metro',    icon: Building2, description: 'Within 40 km',  features: ['City-wide visibility', 'Great for higher-value items'], popular: true },
  regional: { label: 'Regional', icon: Map,       description: 'Within 100 km', features: ['State-level reach', 'Maximum local exposure'] },
};
const TIER_ORDER = ['local', 'metro', 'regional'];

// Fallback config (cents, keyed by duration hours) if the pricing endpoint
// fails — must match backend/scripts/seed_boost_pricing.py.
const FALLBACK_CONFIG = {
  local:    { radius_km: 15,  durations: { 24: 200, 48: 400,  168: 1000 } },
  metro:    { radius_km: 40,  durations: { 24: 300, 48: 600,  168: 1400 } },
  regional: { radius_km: 100, durations: { 24: 500, 48: 1000, 168: 2100 } },
};

const DURATIONS = [
  { hours: 24,  label: '24 hours' },
  { hours: 48,  label: '48 hours', popular: true },
  { hours: 168, label: '7 days' },
];

// Points cost = price in cents (1 pt = 1 cent)
function priceCents(config, tier, hours) {
  if (!config || !tier || !hours) return null;
  const cents = config[tier]?.durations?.[hours];
  return typeof cents === 'number' ? cents : null;
}

function formatDollars(cents) {
  if (cents == null) return null;
  return (cents / 100).toFixed(2);
}

function formatRemaining(expiryStr) {
  if (!expiryStr) return null;
  const diff = new Date(expiryStr) - new Date();
  if (diff <= 0) return '0h';
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  if (hours <= 48) return `${hours}h`;
  return `${Math.ceil(hours / 24)}d`;
}

// ─────────────────────────────────────────────────────────────────────────────

export const BoostModal = ({ item, isOpen, onClose, onSuccess }) => {
  const { api } = useAuth();
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedHours, setSelectedHours] = useState(null);
  const [payMethod, setPayMethod] = useState('cash'); // 'cash' | 'points'
  const [pointsBalance, setPointsBalance] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [configLoading, setConfigLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedTier(null);
      setSelectedHours(null);
      setPayMethod('cash');
      api.get('/points/balance').then(r => setPointsBalance(r.data.points_balance ?? 0)).catch(() => {});

      setConfigLoading(true);
      api.get('/inventory/boost-pricing')
        .then(r => {
          const cfg = r.data?.config;
          if (cfg && typeof cfg === 'object') {
            // Backend stores duration keys as strings; normalise to numbers (hours)
            const normalised = {};
            for (const [tierKey, tierVal] of Object.entries(cfg)) {
              const durations = {};
              for (const [h, cents] of Object.entries(tierVal.durations || {})) {
                durations[Number(h)] = Number(cents);
              }
              normalised[tierKey] = { ...tierVal, durations };
            }
            setConfig(normalised);
          }
        })
        .catch(() => {
          // Fall back to static config silently
        })
        .finally(() => setConfigLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const priceCentsValue = priceCents(config, selectedTier, selectedHours);
  const price = formatDollars(priceCentsValue);
  const pointsCost = priceCentsValue; // 1 pt = 1 cent
  const canAffordPoints = pointsBalance >= (pointsCost || 0);
  const isAlreadyBoosted = Boolean(item.boost_tier);
  const remaining = formatRemaining(item.boost_expires_at || item.boost_expiry);
  const activeTierInfo = isAlreadyBoosted ? TIER_META[item.boost_tier] : null;

  const handleBoost = async () => {
    if (!selectedTier) { toast.error('Please select a tier'); return; }
    if (!selectedHours)  { toast.error('Please select a duration'); return; }
    if (payMethod === 'points' && !canAffordPoints) {
      toast.error('Insufficient points balance'); return;
    }

    setProcessing(true);
    try {
      const response = await api.post(
        `/inventory/${item.id}/boost?tier=${selectedTier}&duration_hours=${selectedHours}&pay_method=${payMethod}`
      );
      toast.success(response.data.message || 'Item boosted successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to boost item');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">

        {/* Header */}
        <div className="p-5 border-b border-[var(--theme-border)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1E3A5F] flex items-center justify-center">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--theme-text-primary)] font-['Manrope']">
                Boost This Listing
              </h2>
              <p className="text-xs text-[var(--theme-text-secondary)]">
                {isAlreadyBoosted ? 'Manage your active boost' : 'Get more buyers to your door'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--theme-hover)] transition-colors"
          >
            <X className="w-4 h-4 text-[var(--theme-text-muted)]" />
          </button>
        </div>

        {/* Item preview */}
        <div className="px-5 pt-4">
          <div className="p-3 rounded-xl bg-[var(--theme-hover)] flex items-center gap-3">
            {item.images?.[0] && (
              <img
                src={typeof item.images[0] === 'object' ? item.images[0].url : item.images[0]}
                alt=""
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-sm text-[var(--theme-text-primary)] truncate">{item.title}</p>
              <p className="text-xs text-[var(--theme-text-secondary)]">${item.price || item.estimated_value}</p>
            </div>
          </div>
        </div>

        {/* ── Already boosted: show stats card ── */}
        {isAlreadyBoosted ? (
          <div className="p-5 space-y-4">
            <div className="p-4 rounded-xl border-2 border-[#C9956C]/50 bg-[#C9956C]/5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#C9956C]" />
                <span className="font-bold text-sm text-[#C9956C] font-['Manrope']">
                  {activeTierInfo?.label || item.boost_tier} Boost Active
                </span>
                {remaining !== null && (
                  <span className="ml-auto text-xs text-[var(--theme-text-muted)] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {remaining} remaining
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 rounded-lg bg-[var(--theme-card)]">
                  <p className="text-lg font-bold text-[var(--theme-text-primary)]">{item.boost_impressions ?? 0}</p>
                  <p className="text-[10px] text-[var(--theme-text-muted)] mt-0.5">Impressions</p>
                </div>
                <div className="p-2 rounded-lg bg-[var(--theme-card)]">
                  <p className="text-lg font-bold text-[var(--theme-text-primary)]">{item.boost_views ?? 0}</p>
                  <p className="text-[10px] text-[var(--theme-text-muted)] mt-0.5">Boost Views</p>
                </div>
                <div className="p-2 rounded-lg bg-[var(--theme-card)]">
                  <p className="text-lg font-bold text-[var(--theme-text-primary)]">{item.views ?? 0}</p>
                  <p className="text-[10px] text-[var(--theme-text-muted)] mt-0.5">Total Views</p>
                </div>
              </div>
              {(item.boost_expires_at || item.boost_expiry) && (
                <p className="text-xs text-[var(--theme-text-muted)] mt-2 text-center">
                  Expires {new Date(item.boost_expires_at || item.boost_expiry).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  })}
                </p>
              )}
            </div>

            <p className="text-xs text-center text-[var(--theme-text-muted)]">
              You can purchase a new boost after your current one expires.
            </p>

            <Button
              variant="outline"
              onClick={onClose}
              className="w-full rounded-full border-[var(--theme-border)]"
            >
              Close
            </Button>
          </div>
        ) : (
          /* ── Purchase flow ── */
          <>
            {/* Description */}
            <div className="px-5 pt-4">
              <p className="text-xs text-[var(--theme-text-secondary)] leading-relaxed">
                Boost your listing to reach more people. Choose a tier based on how far
                you want it shown, and how long you want the boost to run. Boosted
                listings appear higher in the feed and in the recommended section.
              </p>
            </div>

            {/* Step 1: Tier cards */}
            <div className="px-5 pt-4">
              <h3 className="text-sm font-semibold text-[var(--theme-text-primary)] mb-3 flex items-center gap-2 font-['Manrope']">
                <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">1</span>
                Choose Reach
              </h3>
              <div className="space-y-2">
                {TIER_ORDER.map((tierId) => {
                  const meta = TIER_META[tierId];
                  if (!meta) return null;
                  const tierCfg = config[tierId];
                  const Icon = meta.icon;
                  const active = selectedTier === tierId;
                  const fromCents = tierCfg?.durations?.[24];
                  return (
                    <div
                      key={tierId}
                      onClick={() => setSelectedTier(tierId)}
                      className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        active
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                          : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'
                      }`}
                    >
                      {meta.popular && (
                        <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-[#C9956C] text-white text-[10px] font-bold">
                          POPULAR
                        </span>
                      )}
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          active ? 'bg-[#1E3A5F]/10' : 'bg-[var(--theme-hover)]'
                        }`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-[#1E3A5F]' : 'text-[var(--theme-text-muted)]'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm text-[var(--theme-text-primary)] font-['Manrope']">
                              {meta.label}
                              <span className="font-normal text-[var(--theme-text-muted)] ml-1 text-xs">{meta.description}</span>
                            </span>
                            <span className="text-sm font-bold text-[#1E3A5F] flex-shrink-0 ml-2">
                              {fromCents != null ? `from $${formatDollars(fromCents)}` : (configLoading ? '…' : '')}
                            </span>
                          </div>
                          <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">{meta.features[0]}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          active ? 'border-[#1E3A5F] bg-[#1E3A5F]' : 'border-[var(--theme-border)]'
                        }`}>
                          {active && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Duration picker */}
            <div className="px-5 pt-4">
              <h3 className="text-sm font-semibold text-[var(--theme-text-primary)] mb-3 flex items-center gap-2 font-['Manrope']">
                <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">2</span>
                Choose Duration
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {DURATIONS.map((d) => {
                  const active = selectedHours === d.hours;
                  const cents = priceCents(config, selectedTier, d.hours);
                  const tierPrice = cents != null ? formatDollars(cents) : null;
                  return (
                    <div
                      key={d.hours}
                      onClick={() => setSelectedHours(d.hours)}
                      className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                        active
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                          : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'
                      }`}
                    >
                      {d.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-[#1E3A5F] text-white text-[10px] font-bold whitespace-nowrap">
                          POPULAR
                        </span>
                      )}
                      <p className="font-bold text-sm text-[var(--theme-text-primary)] font-['Manrope']">{d.label}</p>
                      {tierPrice && (
                        <p className="text-xs font-bold text-[#059669] mt-1">${tierPrice}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step 3: Payment method */}
            {selectedTier && selectedHours && (
              <div className="px-5 pt-4">
                <h3 className="text-sm font-semibold text-[var(--theme-text-primary)] mb-3 flex items-center gap-2 font-['Manrope']">
                  <span className="w-5 h-5 rounded-full bg-[#1E3A5F] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">3</span>
                  Payment
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    onClick={() => setPayMethod('cash')}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all
                                ${payMethod === 'cash' ? 'border-[#1E3A5F] bg-[#1E3A5F]/5' : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'}`}
                  >
                    <CreditCard className={`w-5 h-5 ${payMethod === 'cash' ? 'text-[#1E3A5F]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className="text-xs font-semibold text-[var(--theme-text-primary)] font-['Manrope']">Card</span>
                    <span className="text-xs font-bold text-[#C9956C]">${price}</span>
                  </div>
                  <div
                    onClick={() => canAffordPoints && setPayMethod('points')}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all
                                ${payMethod === 'points' ? 'border-[#C9956C] bg-[#C9956C]/5' : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'}
                                ${!canAffordPoints ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <Coins className={`w-5 h-5 ${payMethod === 'points' ? 'text-[#C9956C]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className="text-xs font-semibold text-[var(--theme-text-primary)] font-['Manrope']">Points</span>
                    <span className={`text-xs font-bold ${canAffordPoints ? 'text-[#C9956C]' : 'text-[var(--theme-text-muted)]'}`}>
                      {pointsCost?.toLocaleString()} pts
                    </span>
                  </div>
                </div>
                {!canAffordPoints && (
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1.5 text-center">
                    You have {pointsBalance.toLocaleString()} pts — need {pointsCost?.toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="p-5 border-t border-[var(--theme-border)] mt-4 flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 rounded-full border-[var(--theme-border)]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBoost}
                disabled={!selectedTier || !selectedHours || processing}
                className="flex-1 rounded-full bg-[#1E3A5F] hover:bg-[#16304f] text-white"
                data-testid="confirm-boost-btn"
              >
                {processing ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {!selectedTier || !selectedHours
                      ? 'Select Tier & Duration'
                      : payMethod === 'points'
                        ? `Boost Now — ${pointsCost?.toLocaleString()} pts`
                        : `Boost Now — $${price}`}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
